import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { db, auth } from "./firebase-config";
import { getDocs, collection } from "@firebase/firestore";
import { onAuthStateChanged } from 'firebase/auth'; // <-- Import this
import NavigationTabs from './NavigationTabs';
import ServicePage from './ServicePage';
import ResourcePage from './ResourcePage';
import PlaylistsPage from './PlaylistsPage';
import EventsPage from './EventsPage';
import AdminLogin from './AdminLogin';
import AdminDashboard from './admin/AdminDashboard';
import './App.css';

function App() {
  const [resources, setResources] = useState([]);
  const [services, setServices] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [user, setUser] = useState(null); // <-- Add user state
  const [authInitialized, setAuthInitialized] = useState(false); // <-- Add auth check state

  useEffect(() => {
    // This effect sets up the authentication state listener.
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      // Mark auth as initialized once the first check is done.
      setAuthInitialized(true);
    });
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadResources = async () => {
      const querySnapshot = await getDocs(collection(db, "well-being-resources"));
      const loadedResources = querySnapshot.docs
        .map(doc => doc.data())
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setResources(loadedResources);
    };

    const loadServices = async () => {
      const querySnapshot = await getDocs(collection(db, "well-being-services"));
      const loadedServices = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        coords: {
          latitude: doc.data().coords.latitude + (Math.random() - 0.5) * 0.01,
          longitude: doc.data().coords.longitude + (Math.random() - 0.5) * 0.01
        }
      })).sort((a, b) => {
        const nameA = a.is_individual && a.individual_name && a.individual_name.trim() !== "" ? a.individual_name : a.business_name || '';
        const nameB = b.is_individual && b.individual_name && b.individual_name.trim() !== "" ? b.individual_name : b.business_name || '';
        return nameA.localeCompare(nameB);
      });
      setServices(loadedServices);
    };

    const loadPlaylists = async () => {
      const querySnapshot = await getDocs(collection(db, "well-being-playlists"));
      const loadedPlaylists = querySnapshot.docs
        .map(doc => doc.data())
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setPlaylists(loadedPlaylists);
    };

    loadResources();
    loadServices();
    loadPlaylists();
  }, []);

  // Avoid rendering routes until Firebase has checked the auth state.
  // This prevents a flash of the login page for an already authenticated user.
  if (!authInitialized) {
    return null; // Or a loading spinner
  }

  return (
    <Router>
      <div className="App">
        <NavigationTabs />
        <Routes>
          <Route path="/well-being/services" element={<ServicePage key="services" services={services} />} />
          <Route path="/well-being/resources" element={<ResourcePage key="resources" resources={resources} />} />
          <Route path="/well-being/playlists" element={<PlaylistsPage key="playlists" playlists={playlists} />} />
          <Route path="/well-being/events" element={<EventsPage key="events" />} />
          {/* Use the new `user` state to protect the route */}
          <Route path="/well-being/admin/*" element={user ? <AdminDashboard /> : <AdminLogin />} />
          <Route path="*" element={<Navigate to="/well-being/services" />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;